// Read-only "Pickups & Drop-offs" summary tab — reuses the exact card-builder and per-stop block
// already built and pixel-matched to Figma (WAL-HQ-V2 Quoting node 71784:278969) for the Quote
// Detail Overview page's Stop Details modal (see CustomerPortalStopMetaStrip.tsx), so this tab
// shows identical information/design instead of a second, drifting implementation. Sourced from
// the last-SAVED quote snapshot (addressDetails/dimensionsDetails/services), same as that modal —
// not the in-progress edit-form values other tabs on this modal read/write.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { addressDetailsData, dimensionsData, servicesData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';

import { buildStopCards, StopDetailBlock } from '../QuoteDetail/CustomerPortalStopMetaStrip';

const PickupsDropoffsDetails = () => {
    const { t } = useTranslation();
    const { handlingUnitOptions } = useAddressDetails();

    const addressDetails = useSelector(addressDetailsData);
    const dimensionsDetails = useSelector(dimensionsData);
    const selectedServices = useSelector(servicesData);
    const serviceCatalog = useSelector((state: RootState) => state.basic.services);

    // Legacy quotes created before quote_stops rows were synced for every stop (Task 35) have no
    // `stops` array at all — fall back to a synthetic Shipper/Consignee pair (address only, no
    // stop id to attribute dimensions/services against) so the tab still shows the two bookends
    // instead of rendering empty.
    const stops =
        addressDetails?.stops && addressDetails.stops.length > 0
            ? addressDetails.stops
            : [
                  {
                      id: -1,
                      position: 1,
                      city: addressDetails?.shipperCity ?? null,
                      state: addressDetails?.shipperState ?? addressDetails?.shipperStateCode ?? null,
                      fullAddress: addressDetails?.shipperFullAddress ?? null,
                  },
                  {
                      id: -2,
                      position: 2,
                      city: addressDetails?.consigneeCity ?? null,
                      state: addressDetails?.consigneeState ?? addressDetails?.consigneeStateCode ?? null,
                      fullAddress: addressDetails?.consigneeFullAddress ?? null,
                  },
              ];

    const cards = buildStopCards({
        stops,
        selectedServices: selectedServices || [],
        dimensions: dimensionsDetails || [],
        services: serviceCatalog,
        t,
    });

    return (
        <div className="flex flex-col">
            {cards.map((card, idx) => (
                <React.Fragment key={card.position}>
                    {idx > 0 && <div className="h-px bg-utilityGray200 mb-3 mt-6" />}
                    <StopDetailBlock index={idx + 1} card={card} handlingUnitOptions={handlingUnitOptions} />
                </React.Fragment>
            ))}
        </div>
    );
};

export default PickupsDropoffsDetails;
